import { useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import { Trans, useTranslation } from 'react-i18next'

import { ConditionFeatureFlag, ConditionStatementFormData, ConditionTextOperator } from '@makyoapp/makyo-common'
import { ArrowShortRight16, Form24, Table16 } from '@makyoapp/makyo-icon/icon'
import { Button, Tip, useTip } from '@makyoapp/makyo-ui'

import { useActiveLayerContext } from '@/context/__project__/ActiveLayerContext'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { ComponentSettingKey } from '@/shared/builder'
import { filterComponentInBody } from '@/utils/builder/app-builder'
import { getOperatorDefaultOption } from '@/shared/condition'
import { getCurrentFormFieldColumns } from '@/shared/form-utils'

import { ColumnTypeIcon } from '../ColumnTypeIcon'
import { RenderColumn } from '../RenderColumn'
import { VariableColumnDd } from '../VariableSelectV2/VariableColumnDd'
import { VariableWrapper } from '../VariableSelectV2/VariableWrapper'
import { useConditionRepeaterContext } from './ConditionRepeaterContext'
import { useConditionSelectContext } from './ConditionSelectContext'

export function ConditionFormDataDD() {
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()
  const { value, onChange, parentFormId } = useConditionRepeaterContext()
  const { id, setOpen, isRhs, index, isEnd } = useConditionSelectContext()

  const varId = `${id}-form-data`
  const key = isRhs ? 'rhs' : 'lhs'

  const [show, setShow] = useState(false)

  const statement = value.conditions[index]?.[key] as ConditionStatementFormData | undefined
  const isCurrentFlag = statement?.flag === ConditionFeatureFlag.FormData

  const formId = statement?.formId || parentFormId

  const containerSettings = activeLayerClient?.settings._BodySettings
  const form = activeLayerClient?.componentsMap.get(formId as string)

  // get the children elements and settings
  const formChildren = filterComponentInBody(form?._Body || [], (el) => ['form', 'upload'].includes(el._GroupID))
  const formSettings = form?.settings
  const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
  const table = tables.find((t) => t.id === dataSource)

  // generate column options based on form datasource columns
  const colOptions = useMemo(() => {
    if (!table || !containerSettings || !formChildren) return []

    const columns = getCurrentFormFieldColumns(containerSettings, formChildren)

    const columnAdapters = table.columns.filter((c) => columns.includes(c.id))

    return columnAdapters
  }, [containerSettings, formChildren, table])

  const [col, setCol] = useState<string | undefined>(isCurrentFlag ? statement?.col : colOptions?.[0]?.id || '')

  return (
    <VariableWrapper
      id={id ? varId : undefined}
      show={show}
      setShow={setShow}
      title={<Trans>Form Data</Trans>}
      icon={<Form24 width={16} height={16} />}
    >
      <div className='flex-c flex-col'>
        <VariableColumnDd id={id ? `${id}-column` : undefined} setCol={setCol} col={col} colOptions={colOptions} />
        <div className='w-full px-2'>
          <Button
            className='w-full'
            disabled={!col}
            onClick={() => {
              const nextCond = [...value.conditions]
              const isSame = !isRhs ? (nextCond[index].lhs as ConditionStatementFormData)?.col === col : true
              const newColObj = colOptions.find((c) => c.id === col)

              const nextOp = isSame
                ? nextCond[index].operator
                : newColObj && table
                  ? getOperatorDefaultOption(newColObj, table, tables)
                  : ConditionTextOperator.IsNotEmpty

              nextCond[index] = {
                ...nextCond[index],
                [key]: {
                  flag: ConditionFeatureFlag.FormData,
                  col: col ?? '',
                  formId: formId ?? '',
                } as unknown as ConditionStatementFormData,
                operator: nextOp,
              }
              onChange?.({
                isAny: value.isAny,
                conditions: nextCond,
              })
              setShow(false)
              setOpen(false)
            }}
          >
            <Trans>Select</Trans>
          </Button>
        </div>
      </div>
    </VariableWrapper>
  )
}

export function ConditionFormDataStatement({ statement }: { statement: ConditionStatementFormData }) {
  const { t } = useTranslation()
  const { tables } = useDbClientContext()
  const { activeLayerClient } = useActiveLayerContext()

  const formId = statement.formId
  const col = statement.col

  const form = activeLayerClient?.componentsMap.get(formId as string)
  const formSettings = form?.settings
  const dataSource = formSettings?.[ComponentSettingKey.FormDataSource]
  const table = tables.find((t) => t.id === dataSource)
  const column = table?.columns.find((c) => c.id === col)

  const {
    x,
    y,
    strategy,
    context,
    isOpen: isTipOpen,
    placement,
    arrowRef,
    getFloatingProps,
    refs,
    getReferenceProps,
  } = useTip({
    placement: 'top',
  })

  if (!table || !column) return null

  return (
    <>
      <div ref={refs.setReference} className='flex items-center gap-1 min-w-0' {...getReferenceProps()}>
        <div className='text-m-ss uppercase' title={t('Form Data')}>
          <Form24 width={14} height={14} />
        </div>
        <RenderColumn col={column} className='min-w-0' textClassName='txt' withStyle width={14} height={14} />
      </div>

      {isTipOpen &&
        createPortal(
          <Tip
            className='text-m-sm'
            x={x}
            y={y}
            placement={placement}
            strategy={strategy}
            ref={refs.setFloating}
            {...getFloatingProps()}
            context={context}
            arrowRef={arrowRef}
          >
            <div className='flex-c'>
              <div className='flex-c whitespace-nowrap'>
                <Form24 width={14} height={14} />
                <Trans>Form Data</Trans>
              </div>
              <div className='flex-c'>
                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <Table16 width={14} height={14} />
                  {table.name}
                </div>

                <ArrowShortRight16 />
                <div className='flex-c whitespace-nowrap'>
                  <ColumnTypeIcon type={column.type} size={14} />
                  {column.name}
                </div>
              </div>
            </div>
          </Tip>,
          document.body
        )}
    </>
  )
}
